import { useState, useCallback, useEffect, useRef } from 'react';

export type LoadPhase = 'loading' | 'ready' | 'error';

export interface UseVideoLoadStateOptions {
  onLoadingChange?: (loading: boolean) => void;
  onPlayerError?: (error?: unknown) => void;
  resetKey?: unknown;
}

export interface UseVideoLoadStateResult {
  phase: LoadPhase;
  playerVisible: boolean;
  handleMediaReady: () => void;
  handleError: (error?: unknown) => void;
}

export function useVideoLoadState({
  onLoadingChange,
  onPlayerError,
  resetKey,
}: UseVideoLoadStateOptions): UseVideoLoadStateResult {
  const [phase, setPhase] = useState<LoadPhase>('loading');

  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  const onPlayerErrorRef = useRef(onPlayerError);
  onPlayerErrorRef.current = onPlayerError;

  const isManaged = onLoadingChange !== undefined;
  const isManagedRef = useRef(isManaged);
  isManagedRef.current = isManaged;

  useEffect(() => {
    setPhase('loading');
    onLoadingChangeRef.current?.(true);
  }, [resetKey]);

  const handleMediaReady = useCallback(() => {
    setPhase(current => {
      if (current !== 'loading') return current;
      onLoadingChangeRef.current?.(false);
      return 'ready';
    });
  }, []);

  const handleError = useCallback((error?: unknown) => {
    setPhase(current => {
      if (current === 'error') return current;
      if (current === 'loading') {
        onLoadingChangeRef.current?.(false);
      }
      onPlayerErrorRef.current?.(error);
      return 'error';
    });
  }, []);

  const playerVisible = phase === 'ready' || (phase === 'loading' && !isManaged);

  return { phase, playerVisible, handleMediaReady, handleError };
}
